import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { YYYY_MM_DD } from "constants/date";
import * as DATE_PERIOD from "constants/dateFilter";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useFormikContext } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";

import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  DateRangeValue,
  getDateRange,
  getDateRangeWithSetting,
} from "utils/getDateRange";
import { baseDatePeriod } from "utils/getDatePeriod";
import {
  CategorisationHistoryTable,
  CATEGORISATION_HISTORY_VARIANT,
} from "./CategorisationHistoryTable";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { useCurrentGroup } from "hooks/useCurrentGroup";

type CategorisationHistoryFormValues = {
  selectedPeriod: string;
  selectedCategoryId: string;
};

type CategorisationHistoryContentProps = {
  merchantId: string;
  vendorName?: string;
  onClose: () => void;
  onCategoryAssign: (categoryId: string) => void;
  isAssigning: boolean;
  readOnly?: boolean;
};

const CategorisationHistoryContent = ({
  merchantId,
  vendorName,
  onClose,
  onCategoryAssign,
  isAssigning,
  readOnly = false,
}: CategorisationHistoryContentProps) => {
  const { values, setFieldValue } =
    useFormikContext<CategorisationHistoryFormValues>();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroup();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId: entityId! },
    { skip: !entityId }
  );

  const { data: startDateData } = useGetStartingDateQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const { startDate, endDate } = getDateRangeWithSetting(
    values.selectedPeriod as DateRangeValue,
    bookkeepingSettings?.tax_year_end,
    bookkeepingStartDate
  );

  const handleCategorySelect = (categoryId: string) => {
    setFieldValue("selectedCategoryId", categoryId);
  };

  const handleAssign = () => {
    if (values.selectedCategoryId) {
      onCategoryAssign(values.selectedCategoryId);
    }
  };

  const periodList = [
    ...baseDatePeriod,
    { label: "Last 12 months", value: DATE_PERIOD.LAST_12_MONTHS },
  ];

  const value = periodList.find((d) => d.value === values.selectedPeriod);

  return (
    <>
      <Modal.Header>
        <Modal.Title>Categorization history</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body className="t-flex t-flex-col t-gap-4">
        <p className="t-text-body-sm t-text-text-30 t-m-0">
          List of categories associated with the vendor {vendorName}. Select a
          category to assign it to the selected transaction.
        </p>

        <div className="t-flex t-items-center t-gap-2">
          <span className="t-text-body-sm t-text-text-60">
            Show transactions for:
          </span>
          <Combobox
            name="selectedPeriod"
            withForm
            placeholder="Select period"
            className="t-w-40"
            components={{ ClearIndicator: () => null }}
            options={periodList}
            value={value}
            menuPortalTarget={document.body}
            size="small"
          />
        </div>

        <CategorisationHistoryTable
          merchantId={merchantId}
          startDate={startDate?.format(YYYY_MM_DD)}
          endDate={endDate?.format(YYYY_MM_DD)}
          variant={CATEGORISATION_HISTORY_VARIANT.RADIO}
          selectedCategoryId={values.selectedCategoryId}
          onCategorySelect={handleCategorySelect}
        />
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Button onClick={onClose}>Cancel</Button>
        {!readOnly && (
          <Button
            customType="primary"
            onClick={handleAssign}
            disabled={!values.selectedCategoryId || isAssigning}
            isLoading={isAssigning}
          >
            Assign category
          </Button>
        )}
      </Modal.FooterButtonGroup>
    </>
  );
};

type CategorisationHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
  vendorName?: string;
  onCategoryAssign: (categoryId: string) => void;
  isAssigning?: boolean;
  readOnly?: boolean;
};

export const CategorisationHistoryModal = ({
  isOpen,
  onClose,
  merchantId,
  vendorName,
  onCategoryAssign,
  isAssigning = false,
  readOnly = false,
}: CategorisationHistoryModalProps) => {
  const initialValues: CategorisationHistoryFormValues = {
    selectedPeriod: DATE_PERIOD.LAST_12_MONTHS,
    selectedCategoryId: "",
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content
        size="xl"
        asChild
        useCustomOverlay
        onInteractOutside={(e) => e.preventDefault()}
      >
        <FormikForm initialValues={initialValues} onSubmit={() => {}}>
          <CategorisationHistoryContent
            merchantId={merchantId}
            vendorName={vendorName}
            onClose={onClose}
            onCategoryAssign={onCategoryAssign}
            isAssigning={isAssigning}
            readOnly={readOnly}
          />
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
